import { useState, useEffect, useRef } from "react";

// GA4 Event tracking - fires to Google Analytics
const fireGtagEvent = (eventName, eventParams = {}) => {
  if (typeof window.gtag === "function") {
    window.gtag("event", eventName, eventParams);
  }
  console.log(`[GA4 Event] ${eventName}`, eventParams);
};

// Role-specific content configuration
const ROLE_CONFIG = {
  talent: {
    headline: "Your talent deserves structure.",
    bullets: [
      "Are you visible beyond Instagram?",
      "Can enterprises verify your credibility instantly?",
      "Are opportunities finding you — or are you chasing them?"
    ],
    ctaText: "Build My Verified Talent Profile",
    ctaEmoji: "🚀"
  },
  trainer: {
    headline: "Your expertise deserves scale.",
    bullets: [
      "Do talents discover you easily?",
      "Is your business scattered across platforms?",
      "Are you positioned as authority?"
    ],
    ctaText: "Establish My Trainer Presence",
    ctaEmoji: "🎯"
  },
  enterprise: {
    headline: "Your production deserves centralization.",
    bullets: [
      "Is casting, production & coordination fragmented?",
      "Are you managing workflows across WhatsApp, Email & Sheets?",
      "Is everything documented in one structured system?"
    ],
    ctaText: "Centralize My Production Workflow",
    ctaEmoji: "🏢"
  }
};

// Premium Progress Bar
const ProgressBar = ({ step, total }) => {
  const progress = (step / total) * 100;
  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <div className="h-1 bg-white/10">
        <div
          className="h-full bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="absolute top-4 left-1/2 -translate-x-1/2">
        <span className="text-white/50 text-xs font-medium tracking-widest uppercase">
          Step {step} of {total}
        </span>
      </div>
    </div>
  );
};

// Slide wrapper with premium fade transition
const Slide = ({ isVisible, children }) => (
  <div
    className={`fixed inset-0 w-full h-screen transition-opacity duration-300 ease-in-out ${
      isVisible ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
    }`}
    style={{
      backgroundImage: "url(/funnel/ecosystem.jpg)",
      backgroundSize: "cover",
      backgroundPosition: "center"
    }}
  >
    {/* Premium dark cinematic overlay */}
    <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90" />
    
    {/* Content */}
    <div className="relative z-20 h-full flex flex-col items-center justify-center px-6 text-center text-white">
      {children}
    </div>
  </div>
);

// Role selection button - premium glass style
const RoleButton = ({ onClick, label, isSelected }) => (
  <button
    onClick={onClick}
    className={`group relative px-10 py-5 min-w-[180px] rounded-2xl font-semibold text-lg transition-all duration-300 ${
      isSelected
        ? "bg-white text-black scale-105 shadow-2xl shadow-white/20"
        : "bg-white/5 backdrop-blur-md border border-white/20 text-white hover:bg-white/10 hover:border-white/40 hover:scale-[1.02]"
    }`}
  >
    {label}
  </button>
);

// Premium CTA Button
const PrimaryCTA = ({ onClick, emoji, label }) => (
  <button
    onClick={onClick}
    data-testid="funnel-final-cta"
    className="group relative px-10 py-5 rounded-2xl text-lg md:text-xl font-bold bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 hover:from-violet-500 hover:via-purple-500 hover:to-fuchsia-500 text-white transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] shadow-2xl shadow-purple-500/30"
  >
    <span className="mr-2">{emoji}</span>
    {label}
  </button>
);

// Transition/Loading Screen
const TransitionScreen = ({ isVisible }) => (
  <div
    className={`fixed inset-0 z-50 bg-black flex flex-col items-center justify-center transition-opacity duration-300 ${
      isVisible ? "opacity-100" : "opacity-0 pointer-events-none"
    }`}
  >
    <div className="relative">
      {/* Animated loader */}
      <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
    </div>
    <p className="mt-8 text-white/80 text-lg font-medium tracking-wide">
      Preparing your dashboard...
    </p>
  </div>
);

export default function Funnel() {
  // State
  const [currentSlide, setCurrentSlide] = useState(0); // 0=hook, 1=pain, 2=close
  const [selectedRole, setSelectedRole] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const roleConfig = selectedRole ? ROLE_CONFIG[selectedRole] : null;
  const totalSlides = 3;
  
  // Refs to track fired events (ensure each fires only once)
  const firedEvents = useRef({
    funnel_view: false,
    funnel_role_selected: {},
    funnel_slide_viewed: {},
    funnel_completed: false,
    funnel_cta_clicked: false
  });

  // 1) funnel_view - fire once on page load
  useEffect(() => {
    if (!firedEvents.current.funnel_view) {
      fireGtagEvent("funnel_view");
      firedEvents.current.funnel_view = true;
    }
  }, []);

  // Track slide views
  useEffect(() => {
    const slideKey = `${currentSlide}-${selectedRole || 'none'}`;
    if (!firedEvents.current.funnel_slide_viewed[slideKey]) {
      fireGtagEvent("funnel_slide_viewed", { 
        slide: currentSlide + 1, 
        role: selectedRole || 'none' 
      });
      firedEvents.current.funnel_slide_viewed[slideKey] = true;
    }
  }, [currentSlide, selectedRole]);

  // funnel_completed - fire when reaching final slide
  useEffect(() => {
    if (currentSlide === 2 && selectedRole && !firedEvents.current.funnel_completed) {
      fireGtagEvent("funnel_completed", { role: selectedRole });
      firedEvents.current.funnel_completed = true;
    }
  }, [currentSlide, selectedRole]);

  // Handlers
  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    
    // 2) funnel_role_selected
    if (!firedEvents.current.funnel_role_selected[role]) {
      fireGtagEvent("funnel_role_selected", { role });
      firedEvents.current.funnel_role_selected[role] = true;
    }
    
    // Move to slide 2
    setCurrentSlide(1);
  };

  const handleContinueToClose = () => {
    setCurrentSlide(2);
  };

  const handleFinalCTA = () => {
    // funnel_cta_clicked
    if (!firedEvents.current.funnel_cta_clicked) {
      fireGtagEvent("funnel_cta_clicked", { role: selectedRole });
      firedEvents.current.funnel_cta_clicked = true;
    }
    
    // Show transition screen
    setIsTransitioning(true);
    
    // Redirect after 1.2 seconds
    setTimeout(() => {
      window.location.href = "https://talentmeet.in";
    }, 1200);
  };

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black">
      {/* Progress Bar */}
      <ProgressBar step={currentSlide + 1} total={totalSlides} />

      {/* SLIDE 1 - Emotional Hook + Role Selection */}
      <Slide isVisible={currentSlide === 0}>
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight">
            India's Verified
            <br />
            <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-fuchsia-400 bg-clip-text text-transparent">
              Talent Ecosystem
            </span>
          </h1>
          
          <p className="text-lg md:text-xl lg:text-2xl text-white/60 mb-14 max-w-xl mx-auto font-light">
            Are you building your career… or waiting to be discovered?
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <RoleButton
              onClick={() => handleRoleSelect("talent")}
              label="Talent"
              isSelected={selectedRole === "talent"}
            />
            <RoleButton
              onClick={() => handleRoleSelect("trainer")}
              label="Trainer"
              isSelected={selectedRole === "trainer"}
            />
            <RoleButton
              onClick={() => handleRoleSelect("enterprise")}
              label="Enterprise"
              isSelected={selectedRole === "enterprise"}
            />
          </div>
          
          {/* Social proof */}
          <p className="text-white/30 text-sm tracking-wider">
            Trusted by growing creators and production teams.
          </p>
        </div>
      </Slide>

      {/* SLIDE 2 - Role-Specific Pain Trigger */}
      <Slide isVisible={currentSlide === 1}>
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-12 leading-tight">
            {roleConfig?.headline}
          </h1>
          
          <div className="space-y-6 mb-14 text-left max-w-lg mx-auto">
            {roleConfig?.bullets.map((bullet, idx) => (
              <div 
                key={idx} 
                className="flex items-start gap-4 text-lg md:text-xl text-white/70"
                style={{ 
                  animationDelay: `${idx * 150}ms`,
                  animation: 'fadeInUp 0.5s ease-out forwards',
                  opacity: 0
                }}
              >
                <span className="text-purple-400 mt-1">•</span>
                <span>{bullet}</span>
              </div>
            ))}
          </div>
          
          <button
            onClick={handleContinueToClose}
            className="px-10 py-4 rounded-xl text-lg font-semibold bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 hover:border-white/40 transition-all duration-300 hover:scale-[1.02]"
          >
            Continue
          </button>
        </div>
        
        {/* Inline animation styles */}
        <style>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </Slide>

      {/* SLIDE 3 - Power Close + Dynamic CTA */}
      <Slide isVisible={currentSlide === 2}>
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Structured Profiles.
            <br />
            <span className="text-white/80">Verified Network.</span>
            <br />
            <span className="text-white/60">Unified Workflow.</span>
          </h1>
          
          <div className="h-8" />
          
          <PrimaryCTA
            onClick={handleFinalCTA}
            emoji={roleConfig?.ctaEmoji}
            label={roleConfig?.ctaText}
          />
          
          <p className="mt-10 text-white/30 text-sm tracking-wider">
            Early access phase now open.
          </p>
        </div>
      </Slide>

      {/* Transition Screen */}
      <TransitionScreen isVisible={isTransitioning} />

      {/* Bottom progress dots */}
      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-40 flex gap-3">
        {[0, 1, 2].map((idx) => (
          <div
            key={idx}
            className={`h-2 rounded-full transition-all duration-300 ${
              idx === currentSlide
                ? "bg-white w-8"
                : idx < currentSlide
                  ? "bg-white/50 w-2"
                  : "bg-white/20 w-2"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
